import { createServerFn } from "@tanstack/react-start";
import { generateText } from "ai";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { getTutorModel } from "./ai-gateway.server";

const InputSchema = z.object({
  subject: z.string().min(2).max(60),
  level: z.string().min(2).max(20), // e.g. "P5", "S2"
  topic: z.string().min(2).max(120),
  count: z.number().int().min(3).max(15).default(8),
});

export type PracticeQuestion = {
  id: string;
  prompt: string;
  choices: string[];
  answerIndex: number;
  explanation: string;
};

export type PracticeQuiz = {
  title: string;
  subject: string;
  level: string;
  topic: string;
  questions: PracticeQuestion[];
};

function tryParseJson(raw: string): unknown {
  const cleaned = raw
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/```\s*$/i, "")
    .trim();
  try {
    return JSON.parse(cleaned);
  } catch {
    const m = cleaned.match(/\{[\s\S]*\}$/);
    if (m) {
      try { return JSON.parse(m[0]); } catch { /* ignore */ }
    }
    throw new Error("AI did not return valid JSON.");
  }
}

export const generatePracticeQuiz = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => InputSchema.parse(d))
  .handler(async ({ data }): Promise<PracticeQuiz> => {
    const model = getTutorModel("google/gemini-3-flash-preview");

    const system = `You are MAKAI AI, an expert Ugandan teacher.
Generate multiple-choice practice quizzes strictly aligned with the Uganda NCDC (National Curriculum Development Centre) curriculum.
Match the cognitive level to the learner's class (P1–P7 primary, S1–S6 secondary).
Use clear, simple English with local Ugandan context where natural (UGX, Kampala, matooke, boda boda, etc.).
Return ONLY a JSON object — no prose, no markdown fences.`;

    const prompt = `Create a ${data.count}-question multiple-choice practice quiz.

Subject: ${data.subject}
Class level: ${data.level}
Topic: ${data.topic}

Rules:
- Each question has exactly 4 answer choices.
- Exactly one choice is correct.
- "answerIndex" is the 0-based index of the correct choice.
- Provide a short, learner-friendly "explanation" for the correct answer (1–2 sentences).
- Vary difficulty across the set (easy → medium → harder).
- Align with Uganda NCDC ${data.level} ${data.subject} expectations.

Return JSON of this exact shape:
{
  "title": "string",
  "questions": [
    { "prompt": "string", "choices": ["a","b","c","d"], "answerIndex": 0, "explanation": "string" }
  ]
}`;

    const { text } = await generateText({
      model,
      messages: [
        { role: "system", content: system },
        { role: "user", content: prompt },
      ],
    });

    const parsed = tryParseJson(text) as {
      title?: string;
      questions?: Array<{ prompt: string; choices: string[]; answerIndex: number; explanation?: string }>;
    };

    const questions = (parsed.questions ?? [])
      .filter((q) => q && Array.isArray(q.choices) && q.choices.length === 4 && typeof q.prompt === "string")
      .map((q, i) => ({
        id: `q${i + 1}`,
        prompt: q.prompt,
        choices: q.choices.map(String),
        answerIndex: Math.max(0, Math.min(3, Number(q.answerIndex) || 0)),
        explanation: q.explanation ?? "",
      }));

    if (questions.length < 3) {
      throw new Error("Could not generate enough questions. Please try a different topic.");
    }

    return {
      title: parsed.title || `${data.subject} • ${data.topic} (${data.level})`,
      subject: data.subject,
      level: data.level,
      topic: data.topic,
      questions,
    };
  });
